// TemplatesModal.tsx
import React, { useState, useEffect } from "react"
import { Modal, Typography } from "antd"
import { Editor as TiptapEditor } from "@tiptap/react"
import TemplateWizard from "./TemplateWizard.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { TemplateFileListResult } from "types/template.types"

// Define the props interface for TemplatesModal
interface TemplatesModalProps {
  /** Indicates if the template should be used in the master configuration */
  inMaster: boolean
  /** Indicates if the questionnaire is empty */
  qnrEmpty: boolean
  /** Controls the visibility of the modal */
  isTemplateModalVisible: boolean
  /** Function to toggle the visibility of the modal */
  setTemplateModalVisible: (visible: boolean) => void
  /** Indicates if the template files are being listed (loading state) */
  listingTemplateFiles: boolean
  /** The list of template files available */
  templateFiles: TemplateFileListResult
  /** The editor instance used to insert templates into editor */
  editor: TiptapEditor | null
}

/**
 * TemplatesModal component renders a modal for managing Data(Q) templates.
 * It displays a wizard for template selection and configuration or a loader
 * if the template files are still being loaded.
 *
 * @param props - The component props
 * @returns The rendered TemplatesModal component
 */
const TemplatesModal: React.FC<TemplatesModalProps> = (props) => {
  const {
    inMaster,
    qnrEmpty,
    isTemplateModalVisible,
    setTemplateModalVisible,
    listingTemplateFiles,
    templateFiles,
    editor,
  } = props

  const { Title } = Typography

  // Logic to determine modal width based on currentStep
  const [currentStep, setCurrentStep] = useState<number>(0) // Add state for currentStep
  const modalWidth: string | undefined = currentStep > 1 ? "70vw" : undefined // Default width if currentStep is 0 or 1

  // Reset currentStep when modal is closed
  useEffect(() => {
    if (!isTemplateModalVisible) {
      setCurrentStep(0)
    }
  }, [isTemplateModalVisible])

  return (
    <>
      <Modal
        className="template-modal"
        open={isTemplateModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
        width={modalWidth}
      >
        <Title style={{ marginBottom: "0.5em" }} level={4}>
          Data(Q) Templates
        </Title>
        {!listingTemplateFiles ? (
          <TemplateWizard
            inMaster={inMaster}
            qnrEmpty={qnrEmpty}
            templateFiles={templateFiles}
            setTemplateModalVisible={setTemplateModalVisible}
            editor={editor}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader fontSize={24} color="#36C3ED" />
            Loading Templates...
          </div>
        )}
      </Modal>
    </>
  )
}

export default TemplatesModal
